import React from 'react';
import { fetchOverview, fetchDealCards } from './api';
import type { DealCard, IntegrationCard, Overview } from './api';
import { Empty, ErrorBox, LoadingBlock, Pill, fmtNum, plural, useLoad } from './ui';
import { DealRow } from './Deals';

const LOGOS: Record<string, string> = {
  rmis: '/RMIS.png',
  precision_farm: '/Precision%20Farm.png',
  sa_studbook: '/SA%20Studbook.png',
};

const Perf: React.FC<{ label: string; hint: string; value: number | null; unit: string; places?: number }> = ({
  label, hint, value, unit, places = 1,
}) => (
  <li>
    <span className="k"><b>{label}</b><span className="hint">{hint}</span></span>
    {value === null
      ? <span className="v is-empty">Not yet</span>
      : <span className="v">{fmtNum(value, places)}<small>{unit}</small></span>}
  </li>
);

const Integration: React.FC<{ card: IntegrationCard }> = ({ card }) => (
  <li className={`pf-panel pf-int is-${card.state}`}>
    <span className="pf-int-logo">
      {LOGOS[card.key] && card.state !== 'soon'
        ? <img src={LOGOS[card.key]} alt="" loading="lazy" />
        : <span className="pf-mono" aria-hidden="true">SA</span>}
    </span>
    <span>
      <b style={{ display: 'block' }}>{card.name}</b>
      <span className="pf-small" style={{ display: 'block' }}>{card.blurb}</span>
      <span className="pf-small" style={{ display: 'block', color: 'var(--pf-ink-2)', fontWeight: 600, marginTop: 2 }}>
        {card.detail}
      </span>
    </span>
    <Pill tone={card.state === 'active' ? 'is-good' : card.state === 'soon' ? 'is-quiet' : ''}>
      {card.state === 'active' ? 'Connected' : card.state === 'soon' ? 'Soon' : 'Ready'}
    </Pill>
  </li>
);

export const Home: React.FC<{
  firstName: string;
  onOpenDeal: (id: string) => void;
  onAllDeals: () => void;
}> = ({ firstName, onOpenDeal, onAllDeals }) => {
  const ov = useLoad<Overview>(fetchOverview);
  const deals = useLoad<DealCard[]>(fetchDealCards);
  const o = ov.data;
  const open = (deals.data || []).filter(d => d.stage !== 'Complete').slice(0, 3);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="pf-screen">
      <header className="pf-top" style={{ paddingBottom: 0 }}>
        <div className="pf-wrap">
          <div className="pf-brand"><img src="/avcs-logo.png" alt="AVCS" /><span>Producer portal</span></div>
          <h1 className="pf-h1">{greeting}, {firstName}</h1>
          <p className="pf-sub" style={{ marginTop: 4 }}>{o?.producers?.join(' · ') || ' '}</p>
        </div>
      </header>

      <div className="pf-herd">
        <div className="pf-wrap" style={{ paddingTop: 22 }}>
          {ov.loading ? (
            <div role="status" aria-label="Loading"><div className="pf-skel" style={{ height: 66, width: 150, background: '#24415f' }} /></div>
          ) : o ? (
            <>
              <div className="pf-herd-n">{fmtNum(o.animals.through_avcs)}</div>
              <p className="pf-herd-t">
                {o.animals.through_avcs === 1 ? 'animal has' : 'animals have'} gone through AVCS for you
              </p>
            </>
          ) : null}
        </div>
      </div>

      <main className="pf-wrap">
        {ov.error && <div className="pf-section"><ErrorBox message={ov.error} onRetry={ov.retry} /></div>}
        {ov.loading && <div className="pf-section"><LoadingBlock rows={3} /></div>}

        {o && (
          <>
            <section className="pf-section" aria-labelledby="h-herd">
              <div className="pf-section-head"><h2 className="pf-h2" id="h-herd">All time</h2></div>
              <dl className="pf-ledger">
                <div><dt>Deals with AVCS</dt><dd>{fmtNum(o.deals.total)}</dd></div>
                <div><dt>Still on the go</dt><dd>{fmtNum(o.animals.alive)}</dd></div>
                <div><dt>Slaughtered</dt><dd>{fmtNum(o.animals.slaughtered)}</dd></div>
                <div><dt>Movements recorded</dt><dd>{fmtNum(o.animals.movements_recorded)}</dd></div>
              </dl>
            </section>

            <section className="pf-section" aria-labelledby="h-perf">
              <div className="pf-section-head">
                <h2 className="pf-h2" id="h-perf">How your animals perform</h2>
                {o.performance.deals_measured ? (
                  <span className="pf-small">{plural(o.performance.deals_measured, 'deal')} measured</span>
                ) : null}
              </div>
              <ul className="pf-ledger">
                <Perf label="Daily weight gain" hint="Average kg gained per animal per day" value={o.performance.avg_daily_gain_kg} unit="kg/day" places={2} />
                <Perf label="Time in the feedlot" hint="Average days on feed" value={o.performance.avg_days_in_feedlot} unit="days" places={0} />
                <Perf label="Feed conversion" hint="Kg of feed for each kg gained. Lower is better" value={o.performance.feed_conversion} unit=":1" places={2} />
                <Perf label="Dressing percentage" hint="Carcass weight as a share of live weight" value={o.performance.dressing_percentage} unit="%" places={1} />
              </ul>
            </section>
          </>
        )}

        <section className="pf-section" aria-labelledby="h-open">
          <div className="pf-section-head">
            <h2 className="pf-h2" id="h-open">Your deals</h2>
            {(deals.data?.length || 0) > 0 && <button type="button" className="pf-link" onClick={onAllDeals}>See all</button>}
          </div>
          {deals.loading ? <LoadingBlock rows={2} /> : deals.error ? (
            <ErrorBox message={deals.error} onRetry={deals.retry} />
          ) : (deals.data?.length || 0) === 0 ? (
            <Empty title="No deals yet">When AVCS sets up a deal for you it will show here.</Empty>
          ) : (
            <ul className="pf-list">
              {(open.length ? open : (deals.data || []).slice(0, 3)).map(d => (
                <li key={d.id}><DealRow deal={d} onOpen={() => onOpenDeal(d.id)} /></li>
              ))}
            </ul>
          )}
        </section>

        {o && (
          <section className="pf-section" aria-labelledby="h-int">
            <div className="pf-section-head"><h2 className="pf-h2" id="h-int">Connected services</h2></div>
            <ul className="pf-list">{o.integrations.map(c => <Integration key={c.key} card={c} />)}</ul>
          </section>
        )}
      </main>
    </div>
  );
};
